import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
  HttpEventType,
} from '@angular/common/http';

import { catchError, delay, map, Observable, throwError } from 'rxjs';
import { AuthService } from '../shared/auth.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { LoadingService } from 'src/app/shared/services/loading/loading.service';

/** Pass untouched request through to the next request handler. */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private toastService: ToastService,
    private authService: AuthService,
    private loadingService: LoadingService
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Verificar se deve pular o loading
    const shouldSkipLoading = req.headers.has('X-Skip-Loading') ||
      req.url.includes('notifications/me') ||
      req.url.includes('service-order-chats');

    if (!shouldSkipLoading) {
      this.loadingService.show();
    }

    const token = this.authService.getAuthorizationToken();
    const isRefreshRequest = req.url.includes('main-auth/refresh');

    // Se estier faltando um minuto para expirar solicitar refresh token.
    // A renovacao em si usa o REFRESH TOKEN guardado (nao o access token
    // vigente) — ver AuthService.refreshMainToken().
    if (
      token &&
      !isRefreshRequest &&
      !this.authService.isTokenExpired(token) &&
      this.authService.isTokenExpired(token, 60)
    ) {
      this.authService.refreshMainToken().subscribe({
        next: (response) => {
          if (response && response.access_token) {
            window.localStorage.setItem(
              '@gmt:access_token',
              response.access_token
            );
          }
          // O backend rotaciona o refresh token a cada uso (single-use); sem
          // persistir o novo, a proxima renovacao falharia com o token antigo
          // ja consumido.
          if (response && response.refresh_token) {
            window.localStorage.setItem(
              '@gmt:refresh_token',
              response.refresh_token
            );
          }
        },
        error: (e) => {
          console.error(e);
        },
      });
    }

    if (
      token &&
      this.authService.isTokenExpired(token) &&
      !req.url.includes('main-auth')
    ) {
      this.authService.logout();
    }

    // Se o token for valido e não estiver expirado
    if (token && !this.authService.isTokenExpired(token)) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    // Se houver tenant no token, enviar x-tenant-id (isolamento igual ABCX)
    const tenantId = this.authService.getTenantId();
    if (tenantId) {
      req = req.clone({
        setHeaders: {
          'x-tenant-id': tenantId,
        },
      });
    }

    return next
      .handle(req)
      .pipe(catchError((error) => this.handleError(error, req)))
      .pipe(delay(0))
      .pipe(
        map<HttpEvent<any>, any>((evt: HttpEvent<any>) => {
          if (evt.type === HttpEventType.UploadProgress) {
            this.loadingService.setProgress(Math.round(100 * evt.loaded / (evt?.total ?? 0)));
          }

          if (evt instanceof HttpResponse) {
            const shouldSkipLoading = req.headers.has('X-Skip-Loading') ||
              req.url.includes('notifications/me') ||
              req.url.includes('service-order-chats');

            if (!shouldSkipLoading) {
              this.loadingService.hide();
            }
          }

          return evt;
        })
      );
  }

  private handleError(error: HttpErrorResponse, req: HttpRequest<any>) {
    const shouldSkipLoading = error.url?.includes('notifications/me') ||
      error.url?.includes('service-order-chats');

    if (!shouldSkipLoading) {
      this.loadingService.hide();
    }

    if (error.status === 401 && req.method === 'GET') {
      console.log('Permissão negada (GET silenciado):', error.error?.message);
      return throwError(() => error);
    }

    if (error.status === 404 && req.url.includes('settings/GOOGLE_MAPS')) {
      return throwError(() => error);
    }

    console.log(JSON.stringify(error.error));

    if (error.error instanceof ErrorEvent) {
      console.error('Ocorreu um erro:', error.error.message);
    } else if (error?.error?.message) {
      if (error.status !== 409) {
        this.toastService.showWarn(`${error.error.message}`);
      }
    } else if (error?.error?.status === 'error') {
      this.toastService.showWarn(`${error.error.message}`);
    } else if (error?.error?.message === 'Too many requests.') {
    } else {
      console.log(JSON.stringify(error.error));
      this.toastService.showError(
        `Erro no servidor, código do erro ${error.status}`
      );
      console.error(
        `Código do erro ${error.status}`,
        `Erro: ${error.error?.message}`
      );
    }

    if (error.status === 409) {
      return throwError(() => error);
    }

    return throwError('Ocorreu um erro: ' + error.error?.message);
  }
}
